import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import moment from 'moment';

import { showMsg } from '../../services/library';
import InventoryService from '../../services/inventoryService';
import DealerService from '../../services/dealerService';
import DashboardService from '../../services/dashboardService';

const REQ_COMPARISON = 'dealerComparison/';

export const fetchDealerComparisonReq = createAsyncThunk(
  `${REQ_COMPARISON}fetchDealerComparisonReq`,
  async (obj, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getDealerComparison(obj);
      if (response?.status) {
        return { data: response.data[0], dealerIds: obj?.allDealerIds };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchImgCountReq = createAsyncThunk(
  `${REQ_COMPARISON}fetchImgCountReq`,
  async ({ dealer }, { rejectWithValue }) => {
    try {
      const response = await InventoryService.getChartData({ dealer_id: dealer?.value });
      if (response?.status) {
        return { chartData: response?.data?.length > 0 ? response?.data[0] : null };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response?.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchPaProfileDealerComparisonReq = createAsyncThunk(
  `${REQ_COMPARISON}fetchPaProfileDealerComparisonReq`,
  async ({ fromdate, todate, dealerIds, allDealerIds, selectedPhotographer }, { rejectWithValue }) => {
    try {
      const response = await DealerService.getPaProfileDealerComparison({
        fromdate: fromdate,
        todate: todate,
        dealerIds,
        selectedPhotographer
      });
      if (response?.status) {
        return { data: response?.data ?? [], dealerIds: allDealerIds };
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

export const fetchAvgTtmNScoreReq = createAsyncThunk(
  `${REQ_COMPARISON}fetchAvgTtmNScoreReq`,
  async ({ dataFilter }, { rejectWithValue }) => {
    try {
      const response = await DashboardService.getAvgTtm({
        dealers: dataFilter
      });
      if (response?.status) {
        return response?.data?.length > 0 ? response.data[0] : {};
      } else {
        showMsg(response?.errors, 'error');
        return rejectWithValue(response.errors);
      }
    } catch (error) {
      showMsg(error, 'error');
      return rejectWithValue(error);
    }
  }
);

const today = moment(new Date()).startOf('day');
const currentWeekStart = today.clone().subtract(1, 'days').startOf('week').toDate();
const currentWeekEnd = today.clone().endOf('day').toDate();
const DealerComparisonSlice = createSlice({
  name: 'dealerComparison',
  initialState: {
    invChartData: null,
    filteredDealers: [],
    filteredDealersComparison: [],
    isDataLoading: true,
    isRowDataComparisionLoading: true,
    isRowDataPAComparisionLoading: true,
    isPaDataLoading: true,
    startDatePAComp: currentWeekStart,
    endDtePAComp: currentWeekEnd,
    widget_AvgTtmNScore: {
      avarageTTM: null,
      avarageTTS: null,
      avgTTMLoading: true
    },
    comparisonTeamModifiedState: [{ value: '', label: '' }],
    selectedPhotographer: null
  },
  reducers: {
    handleWidget_AvgTtmNScore: {
      reducer(state, action) {
        const { avarageTTM, avarageTTS, avgTTMLoading } = action.payload;
        state.widget_AvgTtmNScore = { avarageTTM, avarageTTS, avgTTMLoading };
        return state;
      },
      prepare(payload) {
        return { payload };
      }
    },
    handleRowDataComparisionLoading: {
      reducer(state, action) {
        state.isRowDataComparisionLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleRowDataPAComparisionLoading: {
      reducer(state, action) {
        state.isRowDataPAComparisionLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePaCompDate: {
      reducer(state, action) {
        const [start, end] = action.payload;
        state.startDatePAComp = start;
        state.endDtePAComp = end;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePaCompStartDate: {
      reducer(state, action) {
        state.startDatePAComp = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handlePaCompEndDate: {
      reducer(state, action) {
        state.endDtePAComp = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilteredDealers: {
      reducer(state, action) {
        state.filteredDealers = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleFilteredDealersComparison: {
      reducer(state, action) {
        state.filteredDealersComparison = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsDataLoading: {
      reducer(state, action) {
        state.isDataLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    handleIsPaDataLoading: {
      reducer(state, action) {
        state.isPaDataLoading = action.payload;
      },
      prepare(e) {
        return { payload: e };
      }
    },
    setComparisonModifiedTeam: {
      reducer(state, action) {
        const transformedData = action.payload.map(user => ({
          value: user.user_id,
          label: user.user_displayname
        }));

        state.comparisonTeamModifiedState = transformedData;
      },
      prepare(payload) {
        return { payload };
      }
    },
    setComparisonPhotographer: {
      reducer(state, action) {
        state.selectedPhotographer = action.payload;
      },
      prepare(payload) {
        return { payload };
      }
    }
  },
  extraReducers: builder => {
    builder
      // #region fetchDealerComparisonReq
      .addCase(fetchDealerComparisonReq.fulfilled, (state, action) => {
        const { filteredDealers } = current(state);
        const { data, dealerIds } = action.payload;
        const { dealerTable } = data ?? {};
        const all = [...filteredDealers, ...dealerTable];

        const uniqueArray = all?.reduce((acc, current) => {
          const x = acc.find(item => item.user_id === current.user_id);
          if (!x) {
            acc.push(current);
          }
          return acc;
        }, []);

        // Filter out dealers that are no longer in dealerIds
        const filteredArray = uniqueArray?.filter(item => dealerIds?.includes(item.user_id));
        state.filteredDealers = filteredArray ?? [];
        state.isDataLoading = false;
      })
      .addCase(fetchDealerComparisonReq.rejected, state => {
        state.filteredDealers = [];
        state.isDataLoading = false;
        state.isRowDataComparisionLoading = false;
      })
      // #endregion

      // #region fetchPaProfileDealerComparisonReq
      .addCase(fetchPaProfileDealerComparisonReq.fulfilled, (state, action) => {
        const { filteredDealersComparison } = current(state);
        const { data, dealerIds } = action.payload;
        const all = [...filteredDealersComparison, ...data];

        const uniqueArray = all?.reduce((acc, current) => {
          const x = acc.find(item => item.user_id === current.user_id);
          if (!x) {
            acc.push(current);
          }
          return acc;
        }, []);

        // Filter out dealers that are no longer in dealerIds
        const filteredArray = uniqueArray?.filter(item => dealerIds?.includes(item.user_id));

        state.filteredDealersComparison = filteredArray ?? [];
        state.isPaDataLoading = false;
      })
      .addCase(fetchPaProfileDealerComparisonReq.rejected, state => {
        state.filteredDealersComparison = [];
        state.isPaDataLoading = false;
        state.isRowDataPAComparisionLoading = false;
      })

      // #endregion

      // #region fetchAvgTtmNScoreReq
      .addCase(fetchAvgTtmNScoreReq.pending, state => {
        const { widget_AvgTtmNScore } = current(state);
        state.widget_AvgTtmNScore = {
          ...widget_AvgTtmNScore,
          avarageTTM: null,
          avarageTTS: null,
          avgTTMLoading: true
        };
      })
      .addCase(fetchAvgTtmNScoreReq.fulfilled, (state, action) => {
        const value = action.payload;
        const { widget_AvgTtmNScore } = current(state);
        state.widget_AvgTtmNScore = {
          ...widget_AvgTtmNScore,
          avarageTTM: value?.ttmAverage,
          avarageTTS: value?.ttsAverage,
          avgTTMLoading: false
        };
      })
      .addCase(fetchAvgTtmNScoreReq.rejected, state => {
        const { widget_AvgTtmNScore } = current(state);
        state.widget_AvgTtmNScore = {
          ...widget_AvgTtmNScore,
          avarageTTM: null,
          avarageTTS: null,
          avgTTMLoading: false
        };
      })
      // #endregion

      // #region fetchImgCountReq
      .addCase(fetchImgCountReq.pending, state => {
        state.invChartData = null;
      })
      .addCase(fetchImgCountReq.fulfilled, (state, action) => {
        const { chartData } = action.payload;
        state.invChartData = chartData;
      })
      .addCase(fetchImgCountReq.rejected, state => {
        state.invChartData = [];
      });
    // #endregion
  }
});

export const {
  handleFilteredDealers,
  handleIsDataLoading,
  handleIsPaDataLoading,
  handleFilteredDealersComparison,
  handlePaCompDate,
  handleRowDataComparisionLoading,
  handleRowDataPAComparisionLoading,
  handlePaCompStartDate,
  handlePaCompEndDate,
  handleWidget_AvgTtmNScore,
  setComparisonModifiedTeam,
  setComparisonPhotographer
} = DealerComparisonSlice.actions;
export default DealerComparisonSlice.reducer;
